import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  styled,
} from "@mui/material";
import { Candidate, RegistrationStepTimestampType } from "../interface";
import { differenceInDays } from "date-fns";
import { DRAG_KEY } from "../constants";
import { useDrag } from "../hooks/dragAndDrop";
import { TransitionModal } from "components/modal";
import { useCallback, useMemo, useState } from "react";
import { CandidateEditForm } from "core/candidate/edit-form";

type BoardCardProps = {
  candidate: Candidate;
  from: number;
  draggable?: boolean;
  timestampType: RegistrationStepTimestampType;
};

export const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

export function BoardCard({ candidate, from, draggable, timestampType }: BoardCardProps) {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const { dragProps } = useDrag({
    actionKey: DRAG_KEY,
    dataTransfer: {
      candidate,
      from,
    },
    disabled: !draggable,
  });

  const incompleteMandatoryDocumentsLabel = useMemo(() => {
    if(!candidate.missing_mandatory_documents_count){
      return null;
    }

    if(candidate.missing_mandatory_documents_count <= 0){
      return '0 doc manquant';
    }

    if(candidate.missing_mandatory_documents_count === 1){
      return '1 doc manquant'
    }

    return `${candidate.missing_mandatory_documents_count} docs manquants`
  }, [candidate?.missing_mandatory_documents_count]);

  const completedMandatoryDocuments = candidate.missing_mandatory_documents_count === 0;

  const dateInfo = useMemo(() => {
    if (timestampType === "deletion_date" && candidate.scheduled_deletion_at && !completedMandatoryDocuments) {
      const diffInDays = differenceInDays(new Date(candidate.scheduled_deletion_at), new Date());
      return diffInDays > 0 ? `${diffInDays} jours restants` : "Dernier jour"
    }

    const diffInDays = differenceInDays(new Date(), new Date(candidate.created_at));
    return diffInDays > 0 ? `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}` : "Aujourd'hui"
  }, [timestampType, candidate?.created_at, candidate?.scheduled_deletion_at, completedMandatoryDocuments]);

  const avatarSubstitute = useMemo(() => {
    return candidate.full_name[0].toUpperCase();
  }, [candidate.full_name]);

  const renderAvatar = useCallback(
    () => (
      <Avatar
        alt={candidate.full_name}
        src={candidate.avatar_url}
        sx={{ width: 32, height: 32, backgroundColor: "divider" }}
      >
        {!candidate.avatar_url && avatarSubstitute}
      </Avatar>
    ),
    [avatarSubstitute, candidate.avatar_url, candidate.full_name]
  );

  return (
    <>
      <TransitionModal open={modalIsOpen} onClose={() => setModalIsOpen(false)} maxWidth="lg">
        <CandidateEditForm tempWorkerId={candidate.id} modalMode />
      </TransitionModal>
      <Grid item>
        <Card
          sx={{
            width: "100%",
            minWidth: 220,
            padding: 0,
            backgroundColor: "background.paper",
            cursor: draggable ? "grab" : "default",
            borderRadius: 2,
          }}
          {...dragProps}
          onClick={() => setModalIsOpen(true)}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "start",
                gap: 1.5,
              }}
            >
              {candidate.available ? (
                <StyledBadge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                >
                  {renderAvatar()}
                </StyledBadge>
              ) : (
                renderAvatar()
              )}
              <Box>
                <Typography variant="body2">{candidate.full_name}</Typography>
                <Typography
                  color={"text.secondary"}
                  variant="subtitle1"
                >
                  {dateInfo}
                </Typography>
                {candidate.has_offers && (
                  <Chip
                    label="Candidature"
                    color="info"
                    sx={{
                      marginTop: 1,
                    }}
                  />
                )}
                {incompleteMandatoryDocumentsLabel && candidate.scheduled_deletion_at && (
                    <Chip
                        label={incompleteMandatoryDocumentsLabel}
                        color={candidate.missing_mandatory_documents_count === 0 ? "success" : "warning"}
                        sx={{
                          marginTop: 1,
                        }}
                    />
                )}
                {completedMandatoryDocuments && (
                    <Chip
                        label="Documents complets"
                        color="success"
                        sx={{
                          marginTop: 1,
                        }}
                    />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}
